import { Button, Input } from '@starsoft/common/components';
import { UserPaymentDetailsCardIndicatorProps } from './props';
import { useTranslation } from '../../../../../../../hooks';
import { UserPaymentDetailsStatus } from '../../../../../../../enums';

/**
 * UserPaymentDetailsCardIndicator Component
 * This component is responsible for rendering an indicator within the user payment details card.
 * It displays a button if the payment details status is rejected, otherwise, it displays a radio input.
 *
 * @param {UserPaymentDetailsCardIndicatorProps} props - The properties for the component.
 * @param {boolean} props.isLoading - Indicates if the data is still loading.
 * @param {boolean} props.selected - Indicates if the card is currently selected.
 * @param {object} props.userPaymentDetails - The user payment details object.
 *
 * @returns {JSX.Element} The rendered component displaying the appropriate indicator.
 *
 * @example
 * // Usage example:
 * <UserPaymentDetailsCardIndicator
 *   isLoading={false}
 *   selected={true}
 *   userPaymentDetails={{ status: UserPaymentDetailsStatus.Rejected }}
 * />
 */
export default function UserPaymentDetailsCardIndicator({
  isLoading,
  selected,
  userPaymentDetails,
}: UserPaymentDetailsCardIndicatorProps) {
  const { t } = useTranslation('common');

  // If the user payment details status is rejected, display a button with the 'edit_label' text
  if (userPaymentDetails?.status == UserPaymentDetailsStatus.Rejected) {
    return (
      <Button isTiny useMaxContent type="button">
        {t('edit_label')}
      </Button>
    );
  }

  // Otherwise, display a radio input indicating the selected state and loading status
  return <Input type="radio" checked={selected} isLoading={isLoading} />;
}
